"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ColorHelper = void 0;
const ColorFormat_1 = require("../enums/ColorFormat");
const tokens_1 = require("../libs/tokens");
/** A utility class to help with transformation of colors to various formats */
class ColorHelper {
    /**
     * Behaves like a normal color formatter. However, if the color is reference, it returns the variable name instead of the formatted color.
     * Precise behavior is as follows:
     * - If the color is full reference, returns the variable name
     * - If the color is partial reference, returns the formatted color with references where possible and raw values where not
     * - Only supported partial reference formats are rgb, rgba, smartRgb
     * - Variable names are generated by the provided functions
     */
    static formattedColorOrVariableName(color, allTokens, options) {
        let fullReferenceName = undefined;
        let colorReferenceName = undefined;
        let opacityReferenceName = undefined;
        // Check references first
        const fullToken = (0, tokens_1.sureOptionalReference)(color.referencedTokenId, allTokens, options.allowReferences);
        if (fullToken) {
            fullReferenceName = options.tokenToVariableRef(fullToken);
        }
        else {
            const colorToken = (0, tokens_1.sureOptionalReference)(color.color.referencedTokenId, allTokens, options.allowReferences);
            if (colorToken) {
                colorReferenceName = options.tokenToVariableRef(colorToken);
            }
            const opacityToken = (0, tokens_1.sureOptionalReference)(color.opacity.referencedTokenId, allTokens, options.allowReferences);
            if (opacityToken) {
                opacityReferenceName = options.tokenToVariableRef(opacityToken);
            }
        }
        // If the full value is reference, return the variable name
        if (fullReferenceName) {
            return fullReferenceName;
        }
        // If there are no references, format the color raw
        if (!fullReferenceName && !colorReferenceName && !opacityReferenceName) {
            return this.formattedColor(color, options.colorFormat, options.decimals);
        }
        // If there are partial references, we'll use the references where possible and return the raw format for the rest
        switch (options.colorFormat) {
            case ColorFormat_1.ColorFormat.rgb:
            case ColorFormat_1.ColorFormat.rgba:
            case ColorFormat_1.ColorFormat.smartRgba:
                return this.colorToRgb(options.colorFormat, this.normalizedIntColor(color), color.opacity.measure, options.decimals, colorReferenceName, opacityReferenceName);
            default:
                return this.formattedColor(color, options.colorFormat, options.decimals);
        }
    }
    /**
     * Formats Supernova color token value to a string based on the selected format. For fractional formats, the number of decimals can be specified.
     */
    static formattedColor(color, format, decimals = 3) {
        switch (format) {
            case ColorFormat_1.ColorFormat.hex6:
            case ColorFormat_1.ColorFormat.hex8:
            case ColorFormat_1.ColorFormat.hashHex6:
            case ColorFormat_1.ColorFormat.hashHex8:
            case ColorFormat_1.ColorFormat.smartHex:
            case ColorFormat_1.ColorFormat.smartHashHex:
                return this.colorToHex(format, this.normalizedIntColor(color), color.opacity.measure);
            case ColorFormat_1.ColorFormat.rgb:
            case ColorFormat_1.ColorFormat.rgba:
            case ColorFormat_1.ColorFormat.smartRgba:
                return this.colorToRgb(format, this.normalizedIntColor(color), color.opacity.measure, decimals, null, null);
            case ColorFormat_1.ColorFormat.hsl:
            case ColorFormat_1.ColorFormat.hsla:
            case ColorFormat_1.ColorFormat.smartHsla:
                return this.colorToHsl(format, this.normalizedFractionalColor(color), color.opacity.measure, decimals);
            case ColorFormat_1.ColorFormat.smartUIColor:
                return this.colorToUIColor(this.normalizedIntColor(color), color.opacity.measure, decimals);
        }
    }
    // Convert color to rgb
    static colorToRgb(format, color, alpha, decimals, colorRefReplace, opacityRefReplace) {
        let resultingRgb;
        if (format === ColorFormat_1.ColorFormat.rgba || (format === ColorFormat_1.ColorFormat.smartRgba && alpha < 1)) {
            resultingRgb = `rgba(${colorRefReplace ? colorRefReplace : `${color.r}, ${color.g}, ${color.b}`}, ${opacityRefReplace ? opacityRefReplace : this.roundToDecimals(alpha, decimals)})`;
        }
        else {
            resultingRgb = `rgb(${colorRefReplace ? colorRefReplace : `${color.r}, ${color.g}, ${color.b}`})`;
        }
        return resultingRgb;
    }
    // Convert color to hex
    static colorToHex(format, color, alpha) {
        // Always need hex6
        let resultingHex = `${this.pHex(color.r)}${this.pHex(color.g)}${this.pHex(color.b)}`;
        if (format === ColorFormat_1.ColorFormat.hex8 ||
            format === ColorFormat_1.ColorFormat.hashHex8 ||
            (format === ColorFormat_1.ColorFormat.smartHex && alpha < 1) ||
            (format === ColorFormat_1.ColorFormat.smartHashHex && alpha < 1)) {
            // Add alpha for 8-format
            resultingHex += `${this.pHex(Math.round(alpha * 255))}`;
        }
        if (format === ColorFormat_1.ColorFormat.hashHex6 || format === ColorFormat_1.ColorFormat.hashHex8 || format === ColorFormat_1.ColorFormat.smartHashHex) {
            // Add hash for hash-format
            resultingHex = `#${resultingHex}`;
        }
        return resultingHex;
    }
    // Convert color to HSL
    static colorToHsl(format, color, alpha, decimals) {
        // Calculate HSL values
        const max = Math.max(color.r, color.g, color.b), min = Math.min(color.r, color.g, color.b);
        let h, s, l = (max + min) / 2;
        if (max === min) {
            h = s = 0; // achromatic
        }
        else {
            const delta = max - min;
            s = l > 0.5 ? delta / (2 - max - min) : delta / (max + min);
            if (max === color.r) {
                h = (color.g - color.b) / delta + (color.g < color.b ? 6 : 0);
            }
            else if (max === color.g) {
                h = (color.b - color.r) / delta + 2;
            }
            else if (max === color.b) {
                h = (color.r - color.g) / delta + 4;
            }
            h /= 6;
        }
        let resultingHsl;
        if (format === ColorFormat_1.ColorFormat.hsla || (format === ColorFormat_1.ColorFormat.smartHsla && alpha < 1)) {
            resultingHsl = `hsla(${Math.round(h * 360)}%, ${Math.round(s * 100)}%, ${Math.round(l * 100)}%, ${this.roundToDecimals(alpha, decimals)})`;
        }
        else {
            resultingHsl = `hsl(${Math.round(h * 360)}%, ${Math.round(s * 100)}%, ${Math.round(l * 100)}%)`;
        }
        return resultingHsl;
    }
    // Convert color to UIColor using base constructor
    static colorToUIColor(color, alpha, decimals = 3) {
        let resultingUIColor = `UIColor(rgb: 0x${this.pHex(color.r)}${this.pHex(color.g)}${this.pHex(color.b)})`;
        if (alpha < 1) {
            resultingUIColor += `.withAlphaComponent(${alpha})`;
        }
        return resultingUIColor;
    }
    // Convert color to normalized 0-255 format
    static normalizedIntColor(color) {
        return {
            r: Math.round(color.color.r),
            g: Math.round(color.color.g),
            b: Math.round(color.color.b)
        };
    }
    // Convert color to normalized 0-1 format
    static normalizedFractionalColor(color, decimals = 3) {
        return {
            r: this.roundToDecimals(color.color.r / 255, decimals),
            g: ColorHelper.roundToDecimals(color.color.g / 255, decimals),
            b: ColorHelper.roundToDecimals(color.color.b / 255, decimals)
        };
    }
    // Round half away from zero to a specific number of decimals
    static roundToDecimals(value, decimals) {
        const multiplier = Math.pow(10, decimals);
        const rounded = Math.round(value * multiplier) / multiplier;
        // Convert to string and parse back to number to remove unnecessary trailing zeroes
        return parseFloat(rounded.toFixed(decimals));
    }
    // Return hex value with leading zero if hex is single digit
    static pHex(value) {
        return value.toString(16).padStart(2, '0');
    }
}
exports.ColorHelper = ColorHelper;
